import React, {
  useCallback, useEffect, useLayoutEffect, useRef,
} from 'react';
import {ArrowRightIcon} from '@heroicons/react-v2/24/outline';

import Link from '../Link';
import {Badge} from '../Badge';
import ScrollFade from './ScrollFade';
import {useSidebar} from './SidebarContext';
import {EASING_CLASS, NEUTRAL_PLATE_CLASS, PRESS_PLATE_CLASS} from './geometry';
import {cx} from '../../util';

/**
 * The submenu panel: a full-height column that slides out from behind the
 * sidebar.
 *
 * Rendered once, as a sibling of the sidebar's own panel rather than per item,
 * which is what makes it possible to (a) size it to the viewport instead of to
 * whichever row opened it and (b) sit *underneath* the sidebar in paint order,
 * so opening reads as the panel emerging from behind the nav. Closed, it is
 * translated fully left of its own left edge, which parks it off screen behind
 * an opaque sidebar.
 *
 * It stays mounted while closed so the exit transition can play; contents are
 * swapped only when a new flyout opens.
 */
const SidebarPanel = () => {
  const {
    flyout,
    showLabels,
    holdFlyout,
    closeFlyout,
    handlePointerMove,
    setPanelRect,
  } = useSidebar();
  const panelRef = useRef(null);
  // Keep rendering the last contents through the close transition, otherwise
  // the panel would blank out and then slide away empty.
  const lastFlyout = useRef(null);
  if (flyout) lastFlyout.current = flyout;

  const content = flyout || lastFlyout.current;
  const open = Boolean(flyout);

  const publishPanelRect = useCallback(() => {
    const el = panelRef.current;
    if (!open || !el) {
      setPanelRect(null);
      return;
    }
    const {top, bottom} = el.getBoundingClientRect();
    const left = el.offsetParent
      ? el.offsetParent.getBoundingClientRect().right
      : el.getBoundingClientRect().left;
    setPanelRect({left, top, bottom});
  }, [open, setPanelRect]);

  // Publish the box the menu-aim test measures against.
  //
  // The left edge cannot come from getBoundingClientRect: the panel arrives by
  // sliding in under a transform, so at the moment it opens its rect still
  // reports the parked position far off to the left. Every pointer inside the
  // sidebar then reads as already past the panel's near edge and the aim test
  // short-circuits to false — which is why the safe triangle never engaged.
  //
  // The settled edge is the right edge of the nav panel this is pinned to, and
  // offsetParent gives that untransformed. Top and bottom are unaffected by a
  // horizontal translation, so those can be read directly.
  useLayoutEffect(() => {
    publishPanelRect();
  }, [publishPanelRect, showLabels]);

  useEffect(() => {
    if (!open) return undefined;
    let frame = null;
    const startedAt = performance.now();
    const tick = () => {
      publishPanelRect();
      if (performance.now() - startedAt < 350) {
        frame = requestAnimationFrame(tick);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => {
      if (frame) cancelAnimationFrame(frame);
    };
  }, [open, publishPanelRect, showLabels]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeFlyout();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeFlyout]);

  if (!content) return null;

  const {
    title, items = [], cta, width, onNavigate,
  } = content;

  // Tracked here so child clicks match the header dropdown's event.
  const handleNavigate = (target) => {
    onNavigate?.(target);
    closeFlyout();
  };

  return (
    <div
      ref={panelRef}
      aria-hidden={!open}
      onMouseEnter={holdFlyout}
      onMouseMove={handlePointerMove}
      className={cx(
        `absolute inset-y-0 left-full z-0 flex flex-col border-r border-gray-200
         bg-white transition-transform duration-300`,
        EASING_CLASS,
        width || 'w-[300px]',
        open
          ? 'translate-x-0 shadow-[8px_0_24px_-12px_rgba(15,23,42,0.18)]'
          : 'pointer-events-none -translate-x-full',
      )}
    >
      {title && (
        <div className="shrink-0 px-5 pt-6 pb-2 text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
          {title}
        </div>
      )}

      <ScrollFade className="px-2 pb-4">
        <ul className="flex flex-col gap-0.5">
          {items.map((item) => (
            <li key={item.title}>
              <Link
                href={item.link}
                forceAnchor={item.forceAnchor}
                onClick={() => handleNavigate(item)}
                tabIndex={open ? undefined : -1}
                className="group relative flex items-start gap-3 rounded-lg px-3 py-2.5 focus-visible:outline-none"
              >
                <span className={cx(PRESS_PLATE_CLASS, NEUTRAL_PLATE_CLASS, 'group-focus-visible:bg-gray-100/80')} />
                {item.icon && (
                  <item.icon className="relative mt-px h-[18px] w-[18px] shrink-0 text-gray-400 transition-colors group-hover:text-indigo" />
                )}
                <span className="relative min-w-0">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-medium text-gray-800 group-hover:text-indigo">
                      {item.title}
                    </span>
                    {item.badge && <Badge className="shrink-0 px-1.5 py-0">{item.badge}</Badge>}
                  </span>
                  {item.description && (
                    <span className="mt-0.5 block text-xs leading-snug text-gray-500">
                      {item.description}
                    </span>
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>

      </ScrollFade>

      {/* Pinned footer; h-[66px] matches the account section so the bars align */}
      {cta && (
        <div className="flex h-[66px] shrink-0 flex-col justify-center border-t border-gray-200 bg-white px-2">
          <Link
            href={cta.link}
            onClick={() => handleNavigate(cta)}
            tabIndex={open ? undefined : -1}
            className="group relative flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:text-indigo focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:outline-none"
          >
            <span className={cx(PRESS_PLATE_CLASS, 'group-hover:bg-gray-50')} />
            <span className="relative">{cta.title}</span>
            <ArrowRightIcon className="relative h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default SidebarPanel;
